import { useRef, useState } from "react";
import { ColumnMenu } from "./ColumnMenu";
import { FilterChips } from "./FilterChips";
import { isColumnFiltered, hasAnyFilter } from "./tableTypes";
import { LiveToggle, type LiveBinding } from "./LiveToggle";
import type { Column, DateRangeBinding } from "./tableTypes";

export type { Column, FilterSpec, DateRangeBinding } from "./tableTypes";

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  total: number;
  limit: number;
  offset: number;
  onPageChange: (nextOffset: number) => void;
  rowKey?: (row: T, index: number) => string;
  /** When supplied, the whole row becomes the click target. Keyboard access is
   *  the caller's job -- a real <button> in one cell, never role="button" on
   *  the <tr>, which would break the table's semantics for screen readers. */
  onRowClick?: (row: T) => void;
  selectedRowKey?: string;

  sort?: string;
  dir?: "asc" | "desc";
  onSortChange?: (sortKey: string, dir: "asc" | "desc") => void;

  filters?: Record<string, string>;
  onFilterChange?: (param: string, value: string) => void;
  onClearFilters?: () => void;

  facets?: Record<string, string[]>;
  dateRange?: DateRangeBinding;

  /** Supplied only by tables backed by a polling query. Omitted elsewhere
   *  (Customers, Staff, Team), which renders no live control at all. */
  live?: LiveBinding;
}

export function DataTable<T>({
  columns, rows, total, limit, offset, onPageChange, rowKey,
  sort, dir, onSortChange, filters, onFilterChange, onClearFilters,
  facets, dateRange, onRowClick, selectedRowKey, live,
}: DataTableProps<T>) {
  // Which column's menu is open. Purely presentational state -- every value
  // the menu edits still lives with the caller.
  const [openHeader, setOpenHeader] = useState<string | null>(null);
  const triggers = useRef<Record<string, HTMLButtonElement | null>>({});

  const filtered = hasAnyFilter(columns, filters, dateRange);
  const isEmpty = total === 0;
  const start = offset + 1;
  const end = Math.min(offset + rows.length, total);

  const clearEverything = () => {
    onClearFilters?.();
    setOpenHeader(null);
  };

  return (
    <div>
      <div className="table-toolbar">
        <FilterChips
          columns={columns}
          filters={filters}
          onFilterChange={onFilterChange}
          onClearFilters={onClearFilters}
          dateRange={dateRange}
        />
        {live && <LiveToggle binding={live} offset={offset} limit={limit} />}
      </div>

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr className="header-row">
              {columns.map((c) => {
                const interactive = Boolean((c.sortKey && onSortChange) || c.filter);
                const sorted = Boolean(c.sortKey && sort === c.sortKey);
                const colFiltered = isColumnFiltered(c.filter, filters, dateRange);

                return (
                  <th
                    key={c.header}
                    className={c.numeric ? "num" : undefined}
                    aria-sort={sorted ? (dir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    {interactive ? (
                      <button
                        ref={(el) => { triggers.current[c.header] = el; }}
                        className={`col-btn${sorted ? " sorted" : ""}`}
                        aria-haspopup="menu"
                        aria-expanded={openHeader === c.header}
                        onClick={() =>
                          setOpenHeader((cur) => (cur === c.header ? null : c.header))
                        }
                      >
                        {c.header}
                        {sorted && <span className="sort-mark">{dir === "asc" ? "▲" : "▼"}</span>}
                        {colFiltered && <span className="filter-dot" aria-label="filtered">•</span>}
                        <span className="caret">▾</span>
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr>
                <td colSpan={columns.length} className="empty-cell">
                  {filtered ? (
                    <>
                      No rows match your filters.{" "}
                      <button onClick={clearEverything}>Clear filters</button>
                    </>
                  ) : (
                    "Nothing in this range."
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const key = rowKey ? rowKey(row, i) : String(i);
                const cls = [
                  onRowClick ? "clickable" : "",
                  selectedRowKey !== undefined && key === selectedRowKey ? "selected" : "",
                ].filter(Boolean).join(" ");
                return (
                  <tr
                    key={key}
                    className={cls || undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((c) => (
                      <td key={c.header} className={c.numeric ? "num" : undefined}>
                        {c.render(row)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!isEmpty && (
        <div className="table-footer">
          <button onClick={() => onPageChange(Math.max(0, offset - limit))} disabled={offset === 0}>
            Prev
          </button>
          <span>{start}–{end} of {total}</span>
          <button onClick={() => onPageChange(offset + limit)} disabled={offset + rows.length >= total}>
            Next
          </button>
        </div>
      )}

      {openHeader && triggers.current[openHeader] && (() => {
        const c = columns.find((x) => x.header === openHeader)!;
        return (
          <ColumnMenu
            anchor={triggers.current[openHeader]!}
            header={c.header}
            sortKey={c.sortKey}
            filter={c.filter}
            sort={sort}
            dir={dir}
            onSortChange={onSortChange}
            filters={filters}
            facets={facets}
            onFilterChange={onFilterChange}
            dateRange={dateRange}
            onClose={() => setOpenHeader(null)}
          />
        );
      })()}
    </div>
  );
}
